import { Order } from './order';
import { AiOrderParserWarning } from './ai-order-parser-warning';

export type AiOrderParserItemStatus =
  | 'AWAITING_CONFIGURATION'
  | 'AWAITING_REVIEW'
  | 'PARSING'
  | 'SUBMITTING'
  | 'SUBMIT_OUTCOME_UNKNOWN'
  | 'DONE'
  | 'REJECTED'
  | 'ERROR';

export type SenderAuthVerdict = 'PASS' | 'FAIL' | 'GRAY' | 'PROCESSING_FAILED';

/** SES's authentication verdicts on the inbound mail, absent for older items. */
export interface AiOrderParserSenderAuth {
  spf?: SenderAuthVerdict | null;
  dkim?: SenderAuthVerdict | null;
  dmarc?: SenderAuthVerdict | null;
  spam?: SenderAuthVerdict | null;
}

/**
 * Sender-gate outcome: why the item was (or wasn't) auto-parsed. Absent/null when no
 * config matched at all, a separate, unrelated case.
 */
export type AutoParseDecision =
  | 'AUTO_PARSED'
  | 'MANUAL_UNAUTHENTICATED'
  | 'MANUAL_UNTRUSTED_FORWARDER'
  | 'MANUAL_SPAM_SUSPECTED';

/** One customer's trust counter, computed by the backend at read time. */
export interface AiOrderParserTrustCounter {
  customerId: string;
  cleanSubmissions: number;
  manualReviewRequired: boolean;
}

/**
 * Why an item is in manual review. Computed server-side on every read rather than stored:
 * the threshold is admin-editable, so a persisted label would lie the moment it changes.
 * Only present on AWAITING_REVIEW items.
 */
export interface AiOrderParserTrustStatus {
  /** The effective threshold X the backend applied. */
  threshold: number;
  /** null when neither customer on the order requires manual review. */
  reason?: 'TRUST_RAMP' | 'PERIODIC_CHECK' | null;
  delivery?: AiOrderParserTrustCounter | null;
  billing?: AiOrderParserTrustCounter | null;
}

export interface AiOrderParserItem {
  id: string;
  customerPrefix: string;
  status: AiOrderParserItemStatus;
  originalFilename: string;
  mimeType: string;
  parsedOrder: Order;
  assignedConfigName?: string;
  warnings: AiOrderParserWarning[];
  parserMessage?: string;
  fromEmail?: string;
  subject?: string;
  sourceMessageId?: string;
  /** Username that uploaded the file manually; not set for emailed orders. */
  uploadedBy?: string;
  receivedAt?: string;
  createdAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  postedToErpAt?: string;
  /** Set when the item entered SUBMIT_OUTCOME_UNKNOWN: when the ambiguous ERP attempt happened. */
  submissionAttemptedAt?: string;
  /** ERP order number(s) assigned when the parsed order was posted; normally a single id. */
  erpOrderIds?: string[];
  rejectionReason?: string;
  errorMessage?: string;
  /** True when a reviewer's save actually changed the order; suppresses the clean-submission count. */
  editedByReviewer?: boolean;
  /** Manual-review reason and per-customer counters; only set for AWAITING_REVIEW items. */
  trustStatus?: AiOrderParserTrustStatus;
  /** SES's authentication verdicts on the inbound mail; absent for older items. */
  senderAuth?: AiOrderParserSenderAuth | null;
  /** Sender-gate decision; absent/null when no config matched at all (unrelated to the gate). */
  autoParseDecision?: AutoParseDecision | null;
  /** Free text for this one order, seeded from the mail body at intake; editable, sent to the model only on opt-in. */
  manualInput?: string;
  /** Whether the last parse actually sent the manual input. */
  manualInputUsed?: boolean;
  /** The mail's sender addresses, offered as sender email suggestions; absent for older items. */
  senderEmailCandidates?: string[];
}

export const AI_ORDER_REVIEW_ROUTE = 'order-parser/review';

export interface AiOrderParserItemPage {
  content: AiOrderParserItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}
